import { Search } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, Grid, TextField, useTheme } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import {
  FetchAutocomplete,
  SingleAutocomplete,
} from '@australiangreens/ag-internal-components';
import { Supporter } from '@/types';
import { dummyLookup, toggleExpansion } from '@/util';
import DeleteFromListAccordionContent from '@components/DeleteFromListAccordionContent';
import DrawerAccordion, { AccordionField } from '@components/DrawerAccordion';
import DistributionListPanel from '@components/DistributionListPanel';
import GroupsFetchAutocomplete from '@components/GroupsFetchAutocomplete';
import TagsFetchAutocomplete from '@components/TagsFetchAutocomplete';
import { usePathwaySteps } from '@hooks/usePathways';
import {
  GROUP_NAMES_REPEAT,
  GROUP_NAMES_TOO_SHORT,
  GROUP_OVERLAP_MESSAGE,
  TAG_OVERLAP_MESSAGE,
  TASK_CHOICE_MAPPING_WITH_IDS,
  TASK_KIND_MAPPING_WITH_IDS,
  UPDATE_PATHWAY_OPTIONS,
  UPDATE_ROCKET_STATUS_OPTIONS,
} from '@components/OtherActionsPanelContent/shared/otherActionsPanelConstants';
import type { OtherActionsPanelFormState } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelForm';

export type OtherActionsPanelAccordionsProps = {
  form: OtherActionsPanelFormState;
  otherActionList: string[];
  listId: number;
  listCount: number;
  sortOrder: string;
  sortColumn: string;
  contactIds?: number[];
  selectedContacts?: Supporter[];
  taskNotesLabel: string;
};

export default function OtherActionsPanelAccordions({
  form,
  otherActionList,
  listId,
  listCount,
  sortOrder,
  sortColumn,
  contactIds,
  selectedContacts,
  taskNotesLabel,
}: OtherActionsPanelAccordionsProps) {
  const theme = useTheme();
  const { data: pathwaySteps } = usePathwaySteps();

  const {
    accordionExpansion,
    setAccordionExpansion,
    groupAddEntities,
    groupRemoveEntities,
    newOrExistingGroup,
    newGroupNames,
    groupOverlap,
    groupNamesClash,
    tagAddEntities,
    tagRemoveEntities,
    tagOverlap,
    updateRocketStatus,
    setUpdateRocketStatus,
    updatePathwayStep,
    setUpdatePathwayStep,
    updatePathwayOption,
    setUpdatePathwayOption,
    electionValue,
    levelOfInterest,
    taskKindType,
    setTaskKindType,
    setLevelOfInterest,
    notes,
    isDownloadPdfChecked,
    setIsDownloadPdfChecked,
    deleteFromListChecked,
    setDeleteFromListChecked,
    distributionListNotes,
    setDistributionListNotes,
    distributionListElection,
    setDistributionListElection,
    taskStatusError,
    taskStatusHelperText,
    lookupByElectionName,
    handleNewOrExistingGroupChange,
    handleAutocompleteNewGroupChange,
    handleAutocompleteAddGroupChange,
    handleAutocompleteAddTagChange,
    handleAutocompleteRemoveTagChange,
    handleAutocompleteRemoveGroupChange,
    handleElectionValueChange,
    handleChangeNotes,
  } = form;

  return (
    <Box sx={{ borderBottom: '1px solid #0000001f' }}>
      {otherActionList.map((headerText, index) => {
        const isError =
          (['Add to group(s)', 'Remove from group(s)'].includes(headerText) && groupOverlap) ||
          (['Add tag(s)', 'Remove tag(s)'].includes(headerText) && tagOverlap);
        return (
          <DrawerAccordion
            headerText={headerText}
            key={index}
            expanded={
              headerText === 'Delete from list' && selectedContacts?.length === 0
                ? false
                : accordionExpansion[index]
            }
            onChange={() => setAccordionExpansion(toggleExpansion(accordionExpansion, index))}
            color={isError ? theme.palette.error.main : 'black'}
            disabled={headerText === 'Delete from list' && selectedContacts?.length === 0}
          >
            {headerText === 'Add to group(s)' && (
              <AccordionField>
                <Grid container rowSpacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Grid>
                    <ToggleButtonGroup
                      value={newOrExistingGroup}
                      exclusive
                      onChange={handleNewOrExistingGroupChange}
                      size="small"
                    >
                      <ToggleButton value="new">New</ToggleButton>
                      <ToggleButton value="existing">Existing</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid>
                    {newOrExistingGroup === 'existing' ? (
                      <GroupsFetchAutocomplete
                        minLength={3}
                        groupLabel="Enter group name(s) to search"
                        value={groupAddEntities}
                        onChange={handleAutocompleteAddGroupChange}
                        dataTestidPrefix="getaddgroups"
                        loadingText="Looking for groups..."
                        noOptionsText="No groups found"
                        error={groupOverlap}
                        helperText={groupOverlap ? GROUP_OVERLAP_MESSAGE : ''}
                      />
                    ) : (
                      <FetchAutocomplete
                        lookup={dummyLookup}
                        minLength={1}
                        label="Enter name for new group(s)"
                        value={newGroupNames}
                        onChange={handleAutocompleteNewGroupChange}
                        data-testid="newgroups"
                        loadingText=""
                        noOptionsText=""
                        error={
                          groupNamesClash.length > 0 ||
                          newGroupNames.filter((x) => x.label.length < 3).length > 0
                        }
                        helperText={
                          groupNamesClash.length > 0
                            ? GROUP_NAMES_REPEAT
                            : newGroupNames.filter((x) => x.label.length < 3).length > 0
                              ? GROUP_NAMES_TOO_SHORT
                              : ''
                        }
                        popupIcon={<SearchIcon />}
                        disableIconFlip
                      />
                    )}
                  </Grid>
                </Grid>
              </AccordionField>
            )}
            {headerText === 'Remove from group(s)' && (
              <AccordionField>
                <GroupsFetchAutocomplete
                  minLength={3}
                  groupLabel="Enter group name(s) to search"
                  value={groupRemoveEntities}
                  onChange={handleAutocompleteRemoveGroupChange}
                  dataTestidPrefix="getremovegroups"
                  loadingText="Looking for groups..."
                  noOptionsText="No groups found"
                  error={groupOverlap}
                  helperText={groupOverlap ? GROUP_OVERLAP_MESSAGE : ''}
                />
              </AccordionField>
            )}
            {headerText === 'Add tag(s)' && (
              <AccordionField>
                <TagsFetchAutocomplete
                  minLength={3}
                  tagLabel="Enter tag name(s) to search"
                  value={tagAddEntities}
                  onChange={handleAutocompleteAddTagChange}
                  dataTestidPrefix="getaddtags"
                  loadingText="Looking for tags..."
                  noOptionsText="No tags found"
                  error={tagOverlap}
                  helperText={tagOverlap ? TAG_OVERLAP_MESSAGE : ''}
                />
              </AccordionField>
            )}
            {headerText === 'Remove tag(s)' && (
              <AccordionField>
                <TagsFetchAutocomplete
                  minLength={3}
                  tagLabel="Enter tag name(s) to search"
                  value={tagRemoveEntities}
                  onChange={handleAutocompleteRemoveTagChange}
                  dataTestidPrefix="getremovetags"
                  loadingText="Looking for tags..."
                  noOptionsText="No tags found"
                  error={tagOverlap}
                  helperText={tagOverlap ? TAG_OVERLAP_MESSAGE : ''}
                />
              </AccordionField>
            )}
            {headerText === 'Update Rocket status' && (
              <AccordionField>
                <Autocomplete
                  data-testid="updateRocket"
                  sx={{ width: '100%' }}
                  options={UPDATE_ROCKET_STATUS_OPTIONS}
                  onChange={(_event, newValue) => {
                    setUpdateRocketStatus(newValue);
                  }}
                  filterOptions={(option) => option}
                  value={updateRocketStatus}
                  renderInput={(params) => (
                    <TextField {...params} variant="filled" label={'Select Rocket status'} />
                  )}
                />
              </AccordionField>
            )}
            {headerText === 'Update volunteer pathway' && (
              <AccordionField>
                <Grid sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Autocomplete
                    data-testid="updatePathway"
                    sx={{ width: '100%' }}
                    options={pathwaySteps ?? []}
                    onChange={(_event, newValue) => {
                      setUpdatePathwayStep(newValue);
                    }}
                    filterOptions={(option) => option}
                    value={updatePathwayStep}
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label={'Select a step'} />
                    )}
                  />
                  <Autocomplete
                    data-testid="updatePathwayOption"
                    sx={{ width: '100%' }}
                    options={UPDATE_PATHWAY_OPTIONS}
                    onChange={(_event, newValue) => {
                      setUpdatePathwayOption(newValue);
                    }}
                    filterOptions={(option) => option}
                    value={updatePathwayOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label={'Select an update option'}
                        helperText={
                          updatePathwayOption &&
                          updatePathwayOption.label ===
                            'Add new & move existing people from any step'
                            ? 'Warning: this action will move people backwards on the pathway'
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
              </AccordionField>
            )}
            {headerText === 'Update campaign tasks' && (
              <>
                <AccordionField title="Election">
                  <SingleAutocomplete
                    data-testid="selectElection"
                    onChange={handleElectionValueChange}
                    value={electionValue}
                    label={'Select election'}
                    lookup={lookupByElectionName}
                    sx={{ width: '100%' }}
                    hideButton
                    popupIcon={<Search />}
                    disableIconFlip
                  />
                </AccordionField>
                <AccordionField
                  title={
                    electionValue
                      ? 'Select the task and status you want to set'
                      : 'Select an election to see the task options'
                  }
                />
                {electionValue && (
                  <AccordionField>
                    <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <AccordionField>
                        <Autocomplete
                          data-testid="selectTaskKindType"
                          sx={{ width: '100%' }}
                          options={TASK_KIND_MAPPING_WITH_IDS}
                          onChange={(_event, newValue) => {
                            setTaskKindType(newValue);
                          }}
                          filterOptions={(option) => option}
                          value={taskKindType}
                          renderInput={(params) => (
                            <TextField {...params} variant="filled" label={'Select a task'} />
                          )}
                        />
                      </AccordionField>
                      <AccordionField>
                        <Autocomplete
                          data-testid="selectLevelOfInterest"
                          sx={{ width: '100%' }}
                          options={TASK_CHOICE_MAPPING_WITH_IDS}
                          onChange={(_event, newValue) => {
                            setLevelOfInterest(newValue);
                          }}
                          filterOptions={(option) => option}
                          value={levelOfInterest}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="filled"
                              label={'Select a status'}
                              error={taskStatusError}
                              helperText={taskStatusHelperText}
                            />
                          )}
                        />
                      </AccordionField>
                      <AccordionField>
                        <TextField
                          data-testid="addNotes"
                          id="tasknotes"
                          label={taskNotesLabel}
                          type="text"
                          fullWidth
                          variant="filled"
                          value={notes}
                          onChange={handleChangeNotes}
                        />
                      </AccordionField>
                    </Grid>
                  </AccordionField>
                )}
              </>
            )}
            {headerText === 'Generate distribution list' && (
              <AccordionField>
                <DistributionListPanel
                  isDownloadPdfChecked={isDownloadPdfChecked}
                  setIsDownloadPdfChecked={setIsDownloadPdfChecked}
                  distributionListElection={distributionListElection}
                  setDistributionListElection={setDistributionListElection}
                  distributionListNotes={distributionListNotes}
                  setDistributionListNotes={setDistributionListNotes}
                  listCount={listCount}
                  sortOrder={sortOrder}
                  sortColumn={sortColumn}
                  listId={listId}
                  contactIds={contactIds}
                />
              </AccordionField>
            )}
            {headerText === 'Delete from list' && (
              <AccordionField>
                <DeleteFromListAccordionContent
                  deleteFromListChecked={deleteFromListChecked}
                  onDeleteFromListCheckedChange={setDeleteFromListChecked}
                  listCount={listCount}
                  selectedContacts={selectedContacts}
                />
              </AccordionField>
            )}
          </DrawerAccordion>
        );
      })}
    </Box>
  );
}
